import type { IProfileData } from '#shared/types/user.types';
import type { DatadogRum, RumInitConfiguration } from '@datadog/browser-rum';

export type UserData = Pick<IProfileData, 'id' | 'displayName' | 'email'>;

export interface DatadogRumPlugin {
  setUser: (userData: UserData) => void;
  clearUser: () => void;
}

declare module '#app' {
  interface NuxtApp {
    $datadogRum?: DatadogRumPlugin;
  }
}

// Debug flag to enable/disable console logs
const enableDebugLogs = false;

// Lazy loaded Datadog RUM instance reference
let datadogRumInstance: DatadogRum | null = null;

export default defineNuxtPlugin(async () => {
  const config = useRuntimeConfig().public;

  if (!config.datadogEnabled) {
    // eslint-disable-next-line no-console
    enableDebugLogs && console.log('[Datadog RUM] Disabled via feature flag');
    return;
  }

  const applicationId = config.datadogAppId;
  const clientToken = config.datadogClientToken;
  const env = config.datadogEnv || 'development';
  const service = config.datadogService || 'maxduel-frontend';

  if (!applicationId || !clientToken) {
    console.error('[Datadog RUM] Missing required configuration: applicationId or clientToken');
    return;
  }

  try {
    // Lazy load Datadog RUM library only when monitoring is enabled
    const { datadogRum } = await import('@datadog/browser-rum');
    datadogRumInstance = datadogRum;

    const rumConfig: RumInitConfiguration = {
      applicationId,
      clientToken,
      service,
      env,
      site: 'us5.datadoghq.com',
      defaultPrivacyLevel: 'mask-user-input',
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      compressIntakeRequests: true,
    };

    setTimeout(() => {
      datadogRum.init(rumConfig);

      setTimeout(() => {
        datadogRum.startSessionReplayRecording();
      }, 0);
    }, 0);

    if (enableDebugLogs) {
      // eslint-disable-next-line no-console
      console.log('[Datadog RUM] Initialized successfully', {
        applicationId,
        service,
        env,
      });
    }
  } catch (error) {
    console.error('[Datadog RUM] Initialization failed:', error);
  }

  const setUser = (userData: UserData) => {
    try {
      if (datadogRumInstance?.setUser) {
        datadogRumInstance.setUser({
          id: userData.id,
          name: userData.displayName || undefined,
          email: userData.email || undefined,
        });
      }
    } catch (error) {
      console.error('[Datadog RUM] Failed to set user:', error);
    }
  };

  const clearUser = () => {
    try {
      if (datadogRumInstance?.clearUser) {
        datadogRumInstance.clearUser();
      }
    } catch (error) {
      console.error('[Datadog RUM] Failed to clear user:', error);
    }
  };

  return {
    provide: {
      datadogRum: {
        setUser,
        clearUser,
      } as DatadogRumPlugin,
    },
  };
});
